// because of https://docs.slatejs.org/concepts/12-typescript#multiple-document-models
// @ts-nocheck
/* eslint-disable */

import { lastIndexOf } from 'lodash';
import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Transforms, Range, Editor, Text, Descendant, Node } from 'slate';
import { Slate, Editable, ReactEditor, RenderPlaceholderProps } from 'slate-react';
import { Avatar, Grid, makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';

import { GetMentionsQuery, MentionObject, Network, useGetMentionsQuery } from 'api';
import { useNotify, useSocialConnections } from 'hooks';
import { getImageSrc } from 'helpers';
import palette from 'theme/palette';

const useStyles = makeStyles(theme => ({
  field: {
    background: theme.palette.grey[100],
    padding: theme.spacing(0.5, 2),
    minHeight: '83px !important',
    borderRadius: 4,
  },
  portal: {
    top: '-9999px',
    left: '-9999px',
    position: 'absolute',
    zIndex: 1500,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    boxShadow: '0 1px 5px rgba(0,0,0,.2)',
    backgroundColor: theme.palette.common.white,
  },
  mentionOption: {
    zIndex: 200,
    padding: theme.spacing(0.25),
    borderRadius: theme.spacing(0.5),
    background: theme.palette.common.white,
  },
  selectedMentionOption: {
    zIndex: 200,
    padding: theme.spacing(0.25),
    borderRadius: theme.spacing(0.5),
    background: palette.blueLight,
  },
  blueText: {
    fontWeight: 'bold',
    color: theme.custom.networks.Company,
  },
  normalText: {
    fontWeight: 'normal',
    color: theme.palette.common.black,
  },
}));

export const Portal = ({ children }) => {
  return ReactDOM.createPortal(children, document.getElementById('root'));
};

export type MentionElement = {
  type: 'mention';
  info: GetMentionsQuery['getMention']['items'];
  text: string;
  children: CustomText[];
};
interface Props {
  editor: Editor;
  fieldName: string;
  mentionsFieldName: string;
  placeholder: string;
  slateValue: SlateMentionsValue[];
  renderPlaceholder?(props: RenderPlaceholderProps): JSX.Element;
  onFocus(): void;
  onChange(descendant: Descendant[]): void;
  disabled?: boolean;
}
const MentionsEditor: FC<Props> = ({
  disabled,
  editor,
  onFocus,
  placeholder,
  renderPlaceholder,
  slateValue,
  fieldName,
  mentionsFieldName,
  onChange,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { setFieldValue } = useFormikContext();
  const ref = useRef<HTMLDivElement | null>();

  const [target, setTarget] = useState<Range | undefined>();
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [hasReturnedResults, setHasReturnedResults] = useState(false);

  const notify = useNotify();

  const renderElement = useCallback(props => <Element {...props} />, []);

  const div = document.createElement('div');

  const { linkedinAccounts } = useSocialConnections();
  const token_id = linkedinAccounts?.[0]?.id;

  const { data } = useGetMentionsQuery(
    { token_id, query: search },
    {
      onError: notify.queryError,
    }
  );

  useEffect(() => {
    if (data?.getMention && search?.length > 2) {
      setHasReturnedResults(true);
      setResults(data?.getMention?.items?.slice(0, 5) || []);
    }
  }, [data]);

  useEffect(() => {
    onChangeText();
  }, [slateValue]);

  useEffect(() => {
    if (target) {
      const { anchor, focus } = target;

      const targetLength = focus?.offset - anchor?.offset;

      if (results.length > 0) {
        const el = ref.current;
        const domRange = ReactEditor.toDOMRange(editor, target);
        const rect = domRange.getBoundingClientRect();
        el.style.top = `${rect.top + window.pageYOffset + 24}px`;
        el.style.left = `${rect.left + window.pageXOffset}px`;

        Transforms.setNodes(
          editor,
          { bold: true },
          { match: n => Text.isText(n), split: true, at: target }
        );
      }

      if (!results.length && hasReturnedResults) {
        Transforms.setNodes(
          editor,
          { bold: false },
          { match: n => Text.isText(n), split: true, at: target }
        );
      }

      if (targetLength && !hasReturnedResults) {
        Transforms.setNodes(
          editor,
          { bold: true },
          { match: n => Text.isText(n), split: true, at: target }
        );
      }
    }

    const { selection } = editor;

    Transforms.setNodes(
      editor,
      { bold: false },
      { match: n => Text.isText(n), split: true, at: selection }
    );
  }, [results.length, editor, index, search, target, hasReturnedResults]);

  const renderLeaf = useCallback(props => {
    return <Leaf {...props} />;
  }, []);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (target) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          const prevIndex = index >= results.length - 1 ? 0 : index + 1;
          setIndex(prevIndex);
          break;
        case 'ArrowUp':
          event.preventDefault();
          const nextIndex = index <= 0 ? results.length - 1 : index - 1;
          setIndex(nextIndex);
          break;
        case 'Tab':
        case 'Enter':
          if (target) {
            event.preventDefault();
            Transforms.select(editor, target);
            insertMention(editor, results[index]);
            setTarget(null);
          }
          break;
        case 'Escape':
          event.preventDefault();
          setTarget(null);
          break;
      }
    }
  }

  function insertMention(editor: Editor, info: GetMentionsQuery['getMention']['items']) {
    const mention: MentionElement = {
      type: 'mention',
      info,
      children: [{ type: 'paragraph', text: info.name }],
    };

    Transforms.insertNodes(editor, mention);
    Transforms.move(editor);
  }

  function onChangeValue(value: Descendant[]) {
    onChange(value);

    const { children } = value[value.length - 1];
    const node = children[children.length - 1];

    const { selection } = editor;
    if (selection && Range.isCollapsed(selection)) {
      const path = selection?.anchor?.path;

      if (Text.isText(node)) {
        const { text } = node;
        const lastAsperandIndex = lastIndexOf(text, '@');

        if (lastAsperandIndex === -1) {
          setTarget(null);
          setHasReturnedResults(false);
          setResults([]);
        }

        if (lastAsperandIndex > -1) {
          const searchText = text.substring(lastAsperandIndex + 1);

          const nonSearchText = text.substring(0, lastAsperandIndex);

          const offset = nonSearchText?.length ?? 0;
          const focusOffset = offset + (searchText?.length ?? 0);

          const anchor = { path, offset };
          const focus = { path, offset: focusOffset + 1 };

          const target = { anchor, focus };

          if (searchText) {
            setTarget(target);
            setSearch(searchText);
            setIndex(0);
          }

          if (!searchText) {
            setResults([]);
          }
        }
      }
    }
  }

  function renderOption(result: GetMentionsQuery['getMention']['items'], i: number) {
    return (
      <Grid
        container
        alignItems="center"
        spacing={1}
        key={result.identifier}
        className={i === index ? classes.selectedMentionOption : classes.mentionOption}
      >
        <Grid item>
          <Avatar {...getImageSrc(result.logo)} />
        </Grid>
        <Grid item>{result?.name}</Grid>
      </Grid>
    );
  }

  function handleRenderPlaceholder({ attributes, children }: RenderPlaceholderProps) {
    const materialUIAttributes = {
      ...attributes,
      style: {
        ...attributes.style,
        opacity: theme.palette.action.disabledOpacity,
      },
    };

    return renderPlaceholder ? (
      renderPlaceholder({ attributes: materialUIAttributes, children })
    ) : (
      <span {...materialUIAttributes}>{children}</span>
    );
  }

  function onChangeText() {
    const serialized = slateValue
      .map(node => {
        return Node?.string(node);
      })
      .join('\n');

    setFieldValue(fieldName, serialized);
  }

  function onBlur() {
    let position = 0;
    const mentions: MentionObject[] = [];

    slateValue.forEach(line => {
      line.children.forEach(node => {
        if (node.type === 'mention') {
          const { name, identifier } = node.info;

          length = name.length;

          const mention: MentionObject = {
            identifier: `urn:li:organization:${identifier}`,
            length,
            provider: Network.Linkedin,
            start: position,
          };

          mentions.push(mention);

          position = position + length;
        }
        if (Text.isText(node)) {
          const { text } = node;
          position = position + text.length;
        }
      });
    });

    setFieldValue(mentionsFieldName, mentions);
    onChangeText();
  }

  return (
    <Slate editor={editor} value={slateValue} onChange={onChangeValue}>
      <Editable
        onKeyDown={onKeyDown}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={placeholder}
        renderPlaceholder={handleRenderPlaceholder}
        className={classes.field}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {target && !!results.length && (
        <Portal>
          <div ref={ref} className={classes.portal}>
            {results.slice(0, 5).map((result, i) => {
              return renderOption(result, i);
            })}
          </div>
        </Portal>
      )}
    </Slate>
  );
};

export default MentionsEditor;

function withMentions(editor: Editor) {
  const { isInline, isVoid } = editor;

  editor.isInline = element => {
    return element.type === 'mention' ? true : isInline(element);
  };

  editor.isVoid = element => {
    return element.type === 'mention' ? true : isVoid(element);
  };

  return editor;
}

const Leaf = props => {
  const classes = useStyles();

  return (
    <span {...props.attributes} className={props.leaf.bold ? classes.blueText : classes.normalText}>
      {props.children}
    </span>
  );
};

const Element = props => {
  const { attributes, children, element } = props;
  switch (element.type) {
    case 'mention':
      return <Mention {...props} />;
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const Mention = ({ attributes, element }) => {
  const classes = useStyles();

  return (
    <span {...attributes} contentEditable={false} className={classes.blueText}>
      @{element.info.name}
    </span>
  );
};
